// One date format for the whole platform: DD/MM/YYYY (founder decision,
// 2026-10-04). Before this, ~500 call sites each picked their own —
// 97 hardcoded en-US (MM/DD/YYYY), 88 followed the browser locale — so the
// same upload read 10/4/2026 on one screen and 4.10.2026 on the next.
//
// Built by hand rather than through toLocaleDateString('en-GB'): the output
// must not shift with the browser's ICU data or the user's UI language, and
// tests need it byte-for-byte stable. Always the viewer's local time zone.

export type DateInput = string | number | Date | null | undefined;

const pad = (n: number) => String(n).padStart(2, '0');

/** Parse anything date-like; null when it is missing or unparseable. Also
 *  accepts the raw EXIF shape "YYYY:MM:DD HH:MM:SS" that cameras write. */
export function toDate(input: DateInput): Date | null {
  if (input === null || input === undefined || input === '') return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  if (typeof input === 'string') {
    const exif = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(input);
    if (exif) {
      const [, y, mo, d, h, mi, s] = exif;
      const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s || 0));
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
  }
  const dt = new Date(input);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** 04/10/2026 */
export function formatDate(input: DateInput, fallback = ''): string {
  const d = toDate(input);
  if (!d) return fallback;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** 14:05 (24-hour) — or 14:05:09 with seconds. */
export function formatTime(input: DateInput, opts: { seconds?: boolean } = {}, fallback = ''): string {
  const d = toDate(input);
  if (!d) return fallback;
  const base = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return opts.seconds ? `${base}:${pad(d.getSeconds())}` : base;
}

/** 04/10/2026 14:05 */
export function formatDateTime(input: DateInput, opts: { seconds?: boolean } = {}, fallback = ''): string {
  const d = toDate(input);
  if (!d) return fallback;
  return `${formatDate(d)} ${formatTime(d, opts)}`;
}

/** 04/10 — for compact places where the year is implied (this year). */
export function formatDayMonth(input: DateInput, fallback = ''): string {
  const d = toDate(input);
  if (!d) return fallback;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}

/** 10/2026 */
export function formatMonthYear(input: DateInput, fallback = ''): string {
  const d = toDate(input);
  if (!d) return fallback;
  return `${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}
